import React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { find } from "lodash";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import useLocalStorage from "../../hooks/useLocalStorage";
import useGAEvent from "../../hooks/useGAEvent";
import { useUser } from "../../context/UserContext";
import { LIVE_VARIANT_VERSIONS } from "../../constants/live-variant-versions";
import { getListingFeedProperties } from "../../services/PropertyService";
import { FIVE_MINUTES } from "../../constants/time";

import "./choose-game.scss";

const INITIAL_LIMIT = 1;
const CACHE_TIME = FIVE_MINUTES;

const ChooseGame = () => {
    const { userState } = useUser();
    const [preferredVersion, setPreferredVersion] = useLocalStorage("live-variant-preferred-version");
    const navigate = useNavigate();
    const { sendEvent } = useGAEvent();
    const [zipcode] = "60657";
    const [sortByValue] = "trending";
    const filters = {
        bedrooms: ["1", "2", "3", "4", "5+"],
        bathrooms: ["1", "2", "3", "4+"],
        propertyType: ["condo", "house"],
        priceRange: { min: 0, max: 0 },
    };

    const { data: feedResponse, error: feedError } = useQuery({
        queryKey: [
            "live-variant-listingFeed-properties",
            zipcode,
            sortByValue,
            filters.propertyType,
            filters.priceRange.min,
            filters.priceRange.max,
            filters.bedrooms,
            filters.bathrooms,
            userState?.id,
        ],
        queryFn: ({ pageParam = 1 }) =>
            getListingFeedProperties({
                sort: sortByValue,
                propertyType: filters.propertyType,
                bedrooms: filters.bedrooms,
                bathrooms: filters.bathrooms,
                page: pageParam.toString(),
                limit: INITIAL_LIMIT.toString(),
                userId: userState?.id,
                feedType: "trending",
            }),
        staleTime: 0,
        cacheTime: CACHE_TIME,
    });

    // Handle error
    if (feedError) {
        if (feedError?.response?.status === 500 || feedError?.response?.status === 400) {
            console.log("Currently, There are no properties. Please try again later.");
        }
    }

    const firstImageFeed = feedResponse?.data?.data[0]?.image_urls[0];

    const handleClick = (newVersion) => {
        const previousVersion = find(LIVE_VARIANT_VERSIONS, (version) => version.url === preferredVersion);
        sendEvent("switch_game", {
            version: previousVersion?.gaVersion ?? "classic",
            newVersion: newVersion?.gaVersion ?? "classic",
        });

        if (newVersion === "classic") {
            setPreferredVersion(newVersion);
            window.location.href = "/classic";
        } else {
            setPreferredVersion(newVersion?.url);
            navigate(`/${newVersion?.url}`);

            // do a reload when previous version is classic to update header
            if (preferredVersion === "classic") {
                window.location.reload();
            }
        }
    };

    return (
        <div className="choose-game-container">
            {Object.keys(LIVE_VARIANT_VERSIONS).map((version) => {
                const currentVersion = LIVE_VARIANT_VERSIONS[version];

                return currentVersion?.label === "social" ? (
                    <React.Fragment key={version}>
                        <div
                            className="mb-4 tw-gap-3 tw-flex tw-flex-col tw-shadow-md tw-rounded-md tw-cursor-pointer"
                            onClick={() => handleClick(currentVersion)}
                        >
                            <div className="tw-mx-2">
                                {!firstImageFeed ? (
                                    <Skeleton
                                        highlightColor="#ffffff"
                                        duration={2}
                                        className="tw-animate-pulse tw-rounded-md tw-h-[22rem] tw-w-full"
                                    />
                                ) : (
                                    <img
                                        src={firstImageFeed || currentVersion.icon.src}
                                        alt={currentVersion.title}
                                        className="!tw-rounded-md tw-w-full tw-h-[22rem] tw-object-cover tw-mt-2"
                                    />
                                )}
                            </div>
                            <div className="tw-flex tw-flex-row tw-px-2">
                                <div>
                                    <p className="tw-text-2xl app-text-title">{currentVersion.title}</p>
                                    <p>{currentVersion.description}</p>
                                </div>
                            </div>
                        </div>
                        <div className="tw-text-center tw-mb-7">
                            <h3 className="tw-text-2xl app-text-title">Play a Game</h3>
                            <p className="tw-text-md">
                                Try one of our real estate games to see <br />
                                how well you know the market
                            </p>
                        </div>
                    </React.Fragment>
                ) : (
                    <React.Fragment key={version}>
                        <div
                            className={clsx(
                                "mb-4 gap-3 info-container tw-shadow-md tw-p-2 tw-rounded-md",
                                currentVersion?.disabled && "!tw-hidden"
                            )}
                            onClick={() => handleClick(currentVersion)}
                        >
                            <div className="tw-flex tw-flex-row">
                                <div className="choose-game-image-container">
                                    <img
                                        style={{
                                            width: currentVersion.icon.width,
                                            height: currentVersion.icon.height,
                                        }}
                                        src={currentVersion.icon.src}
                                        alt={currentVersion.title}
                                    />
                                </div>

                                <div className="choose-game-info-text tw-pl-1.5">
                                    <p>
                                        <b>{currentVersion.title}</b>
                                    </p>
                                    <p>{currentVersion.description}</p>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ChooseGame;
